/**
 * Document type mapping for Retsinformation document types
 * Maps document_type_id to display names and metadata
 */

export type DocumentTypeInfo = {
  DocumentTypeId: string;
  Name: string;
  PopularName: string;
  SorteringsId: number;
  DocumentClassificationName: string;
};

export type DocumentClassification = {
  Ids: string;
  Key: string;
  DisplayName: string;
  DocumentTypes: DocumentTypeInfo[];
};

export type RetsinformationDocumentTypeMapping = {
  Classifications: DocumentClassification[];
};

/**
 * Flattened map of document_type_id -> DocumentTypeInfo
 */
const documentTypeMap = new Map<string, DocumentTypeInfo>();

/**
 * Initialize the document type map from the backend mapping structure
 */
const initializeDocumentTypeMap = () => {
  if (documentTypeMap.size > 0) return; // Already initialized

  const mapping: RetsinformationDocumentTypeMapping = {
    Classifications: [
      {
        Ids: "10",
        Key: "regel",
        DisplayName: "Regler",
        DocumentTypes: [
          {
            DocumentTypeId: "10",
            Name: "Lov",
            PopularName: "Lov",
            SorteringsId: 1,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "1480",
            Name: "Lovtidende B dokument",
            PopularName: "Lovtidende B dokument",
            SorteringsId: 1,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "20",
            Name: "Ændringslov",
            PopularName: "Ændringslov",
            SorteringsId: 2,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "30",
            Name: "Lovbekendtgørelse",
            PopularName: "Lovbekendtgørelse",
            SorteringsId: 3,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "40",
            Name: "Datasammenskrivning",
            PopularName: "Datasammenskrivning",
            SorteringsId: 4,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "50",
            Name: "Tekstanmærkning",
            PopularName: "Tekstanmærkning",
            SorteringsId: 5,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "90",
            Name: "Ikrafttræd. bekendtgørelse",
            PopularName: "Ikrafttræd. bekendtgørelse",
            SorteringsId: 6,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "120",
            Name: "Ikrafttræd. anordning",
            PopularName: "Ikrafttræd. anordning",
            SorteringsId: 7,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "270",
            Name: "Ikrafttræd. anordning (GL og FO)",
            PopularName: "Ikrafttræd. anordning (GL og FO)",
            SorteringsId: 8,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "60",
            Name: "Bekendtgørelse",
            PopularName: "Bekendtgørelse",
            SorteringsId: 9,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "100",
            Name: "Anordning",
            PopularName: "Anordning",
            SorteringsId: 10,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "80",
            Name: "Ændringsbekendtgørelse",
            PopularName: "Ændringsbekendtgørelse",
            SorteringsId: 11,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "110",
            Name: "Ændringsanordning",
            PopularName: "Ændringsanordning",
            SorteringsId: 12,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "130",
            Name: "Åbent brev",
            PopularName: "Åbent brev",
            SorteringsId: 14,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "140",
            Name: "Cirkulære (1 sp)",
            PopularName: "Cirkulære (1 sp)",
            SorteringsId: 15,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "150",
            Name: "Cirkulære (2 sp)",
            PopularName: "Cirkulære (2 sp)",
            SorteringsId: 16,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "160",
            Name: "Ændr.cirkulære (1 sp)",
            PopularName: "Ændr.cirkulære (1 sp)",
            SorteringsId: 17,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "170",
            Name: "Ændr.cirkulære (2 sp)",
            PopularName: "Ændr.cirkulære (2 sp)",
            SorteringsId: 18,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "180",
            Name: "Vejledning",
            PopularName: "Vejledning",
            SorteringsId: 19,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "200",
            Name: "Skrivelse",
            PopularName: "Skrivelse",
            SorteringsId: 21,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "210",
            Name: "Cirkulæreskrivelse",
            PopularName: "Cirkulæreskrivelse",
            SorteringsId: 22,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "220",
            Name: "Bekendtgørelse (international)",
            PopularName: "Bekendtgørelse (international)",
            SorteringsId: 23,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "1510",
            Name: "Anordningsbekendtgørelse",
            PopularName: "Anordningsbekendtgørelse",
            SorteringsId: 147,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "1490",
            Name: "Skatteministeriets store vejledninger",
            PopularName: "Stor vejledning",
            SorteringsId: 100,
            DocumentClassificationName: "Regler",
          },
          {
            DocumentTypeId: "-10",
            Name: "Andre",
            PopularName: "Andre",
            SorteringsId: 99,
            DocumentClassificationName: "Regler",
          },
        ],
      },
      {
        Ids: "20",
        Key: "afgoerelse",
        DisplayName: "Afgørelser",
        DocumentTypes: [
          {
            DocumentTypeId: "230",
            Name: "Afgørelse",
            PopularName: "Afgørelse",
            SorteringsId: 24,
            DocumentClassificationName: "Afgørelser",
          },
          {
            DocumentTypeId: "240",
            Name: "Endelig adm. afgørelse",
            PopularName: "Endelig adm. afgørelse",
            SorteringsId: 25,
            DocumentClassificationName: "Afgørelser",
          },
          {
            DocumentTypeId: "250",
            Name: "Udtalelse",
            PopularName: "Udtalelse",
            SorteringsId: 26,
            DocumentClassificationName: "Afgørelser",
          },
          {
            DocumentTypeId: "260",
            Name: "Dom",
            PopularName: "Dom",
            SorteringsId: 27,
            DocumentClassificationName: "Afgørelser",
          },
          {
            DocumentTypeId: "980",
            Name: "Kommentar",
            PopularName: "Kommentar",
            SorteringsId: 99,
            DocumentClassificationName: "Afgørelser",
          },
        ],
      },
      {
        Ids: "50-60",
        Key: "FolketingetsDokumenter",
        DisplayName: "Lov- og beslutningsforslag mv.",
        DocumentTypes: [
          {
            DocumentTypeId: "360",
            Name: "Skriftlig fremsættelse",
            PopularName: "Skriftlig fremsættelse",
            SorteringsId: 41,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "400",
            Name: "Lovforslag som fremsat",
            PopularName: "Lovforslag som fremsat",
            SorteringsId: 42,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "380",
            Name: "Skriftlig fremsættelse af lovforslag",
            PopularName: "Skriftlig fremsættelse af lovforslag",
            SorteringsId: 43,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "420",
            Name: "Betænkning over lovforslag",
            PopularName: "Betænkning over lovforslag",
            SorteringsId: 45,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "1530",
            Name: "Tilføjelse til betænkning",
            PopularName: "Tilføjelse til betænkning",
            SorteringsId: 46,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "440",
            Name: "Beretning over lovforslag",
            PopularName: "Beretning over lovforslag",
            SorteringsId: 47,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "450",
            Name: "Ændringsforslag til 2. behandling (L)",
            PopularName: "Ændringsforslag til 2. behandling (L)",
            SorteringsId: 48,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "430",
            Name: "Tillægsbetænkning over lovforslag",
            PopularName: "Tillægsbetænkning over lovforslag",
            SorteringsId: 50,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "1540",
            Name: "Tilføjelse til tillægsbetænkning",
            PopularName: "Tilføjelse til tillægsbetænkning",
            SorteringsId: 51,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "460",
            Name: "Ændringsforslag til 3. behandling",
            PopularName: "Ændringsforslag til 3. behandling",
            SorteringsId: 52,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "410",
            Name: "Lovforslag som vedtaget",
            PopularName: "Lovforslag som vedtaget",
            SorteringsId: 53,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "370",
            Name: "Selvstændig beretning",
            PopularName: "Selvstændig beretning",
            SorteringsId: 66,
            DocumentClassificationName:
              "Lovforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "480",
            Name: "Beslutningsforslag som fremsat",
            PopularName: "Beslutningsforslag som fremsat",
            SorteringsId: 60,
            DocumentClassificationName:
              "Beslutningsforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "390",
            Name: "Skriftlig fremsættelse af besl. forslag",
            PopularName: "Skriftlig fremsættelse af besl. forslag",
            SorteringsId: 61,
            DocumentClassificationName:
              "Beslutningsforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "500",
            Name: "Betænkning over beslutningsforslag",
            PopularName: "Betænkning over beslutningsforslag",
            SorteringsId: 62,
            DocumentClassificationName:
              "Beslutningsforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "510",
            Name: "Beretning over beslutningsforslag",
            PopularName: "Beretning over beslutningsforslag",
            SorteringsId: 63,
            DocumentClassificationName:
              "Beslutningsforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "520",
            Name: "Ændringsforslag til 2. behandling (B)",
            PopularName: "Ændringsforslag til 2. behandling (B)",
            SorteringsId: 64,
            DocumentClassificationName:
              "Beslutningsforslag med tilh. betænkninger og beretninger",
          },
          {
            DocumentTypeId: "490",
            Name: "Beslutningsforslag som vedtaget",
            PopularName: "Beslutningsforslag som vedtaget",
            SorteringsId: 65,
            DocumentClassificationName:
              "Beslutningsforslag med tilh. betænkninger og beretninger",
          },
        ],
      },
      {
        Ids: "30",
        Key: "FolketingetsOmbudsmandsBeretninger",
        DisplayName: "Folketingets Ombudsmands beretningssager",
        DocumentTypes: [
          {
            DocumentTypeId: "300",
            Name: "Udtalelser",
            PopularName: "Udtalelser",
            SorteringsId: 31,
            DocumentClassificationName: "Folketingets ombudsmand",
          },
          {
            DocumentTypeId: "310",
            Name: "Opfølgning til udtalelser",
            PopularName: "Opfølgning til udtalelser",
            SorteringsId: 32,
            DocumentClassificationName: "Folketingets ombudsmand",
          },
          {
            DocumentTypeId: "320",
            Name: "Inspektionssager",
            PopularName: "Inspektionssager",
            SorteringsId: 33,
            DocumentClassificationName: "Folketingets ombudsmand",
          },
          {
            DocumentTypeId: "330",
            Name: "Opfølgning til inspektionssager",
            PopularName: "Opfølgning til inspektionssager",
            SorteringsId: 34,
            DocumentClassificationName: "Folketingets ombudsmand",
          },
          {
            DocumentTypeId: "340",
            Name: "Egen drift-projekter",
            PopularName: "Egen drift-projekter",
            SorteringsId: 35,
            DocumentClassificationName: "Folketingets ombudsmand",
          },
          {
            DocumentTypeId: "350",
            Name: "Opfølgning til egen drift-projekter",
            PopularName: "Opfølgning til egen drift-projekter",
            SorteringsId: 36,
            DocumentClassificationName: "Folketingets ombudsmand",
          },
        ],
      },
    ],
  };

  // Build the map from all classifications
  mapping.Classifications.forEach((classification) => {
    classification.DocumentTypes.forEach((docType) => {
      documentTypeMap.set(docType.DocumentTypeId, docType);
    });
  });
};

// Initialize on module load
initializeDocumentTypeMap();

/**
 * Get the display name (Name) for a document type ID
 * @param documentTypeId - The document type ID to look up
 * @returns The display name, or undefined if not found
 */
export const getDocumentTypeName = (
  documentTypeId?: string | null
): string | undefined => {
  if (!documentTypeId) return undefined;
  return documentTypeMap.get(documentTypeId)?.Name;
};

/**
 * Get the popular name (PopularName) for a document type ID
 * @param documentTypeId - The document type ID to look up
 * @returns The popular name, or undefined if not found
 */
export const getDocumentTypePopularName = (
  documentTypeId?: string | null
): string | undefined => {
  if (!documentTypeId) return undefined;
  return documentTypeMap.get(documentTypeId)?.PopularName;
};

/**
 * Get the full document type info for a document type ID
 * @param documentTypeId - The document type ID to look up
 * @returns The full DocumentTypeInfo, or undefined if not found
 */
export const getDocumentTypeInfo = (
  documentTypeId?: string | null
): DocumentTypeInfo | undefined => {
  if (!documentTypeId) return undefined;
  return documentTypeMap.get(documentTypeId);
};

/**
 * Get the display name with fallback to popular name, then document type ID
 * @param documentTypeId - The document type ID to look up
 * @returns The display name, popular name, or the ID itself if not found
 */
export const getDocumentTypeDisplayName = (
  documentTypeId?: string | null
): string => {
  if (!documentTypeId) return "Ukendt type";
  const info = documentTypeMap.get(documentTypeId);
  return info?.Name || info?.PopularName || documentTypeId;
};
